import { Theme, Typography } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import React from "react";
import Select, { StylesConfig, components } from "react-select";
import chroma from "chroma-js";
import Highlighter from "react-highlight-words";
import CommonStyles from "../../components/common/CommonStyles";
import { useTheme } from "@mui/material/styles";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    Select: {},
    highlight: {},
    tooltipText: {
      zIndex: -1,
    },
  })
);

const dot = (color: any) => ({
  alignItems: "center",
  display: "flex",
  fontFamily: "'DM Sans', sans-serif !important",
  ":before": {
    backgroundColor: color,
    borderRadius: 10,
    content: '" "',
    display: "block",
    marginRight: 8,
    height: 10,
    width: 10,
  },
});

const SearchableDropdown = ({
  minSearchChars = 3,
  isMulti,
  placeholder,
  defaultValue,
  options,
  handleChange,
  isDisabled,
  isColor,
  width,
  name,
  isError,
  isOpen,
  height,
  helperText = "",
  customClassNames,
  styleProp,
  getInput,
  onFocus,
  customStyle
}: any) => {
  const [inputValue, setInputValue] = React.useState("");
  const debounceRef = React.useRef<any>(null);

  const mode = CommonStyles();
  const theme = useTheme();

  const errorStyle: StylesConfig = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: isDisabled ? "" : "white",
      width: width,
      minWidth: width,
      height: height ? height : 53,
      minHeight: 50,
      border: state.isFocused ? "1px solid tranpsarent" : "1px solid red",
      ":hover": {
        border: "1px solid red",
      },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    placeholder: (defaultStyles) => ({
      ...defaultStyles,
      color: "#888",
    }),
    container: (provided) => ({
      ...provided,
      fontFamily: "'DM Sans', sans-serif !important",
      fontSize: "14px",
    }),
  };

  const customStyles: StylesConfig<any> = {
    control: (styles) => ({
      ...styles,
      backgroundColor: "white",
      width: width,
      minWidth: width,
      height: height ? height : 53,
      minHeight: 50,
      ":hover": {
        border: "1px solid black",
      },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    container: (provided) => ({
      ...provided,
      fontFamily: "'DM Sans', sans-serif !important",
      fontSize: "14px",
    }),
    placeholder: (styles) => ({ ...styles, fontWeight: 400, color: "#c8c8c8 !important" }),
  };

  const colourStyles: StylesConfig<any> = {
    control: (styles) => ({
      ...styles,
      backgroundColor: isDisabled ? "#EAEAEA" : "white",
      width: width,
      minWidth: width,
      height: height ? height : 53,
      minHeight: 50,
      ":hover": {
        border: "1px solid black",
      },
    }),
    container: (provided) => ({
      ...provided,
      fontFamily: "'DM Sans', sans-serif !important",
      fontSize: "14px",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    multiValue: (styles, { data }) => {
      const color = chroma(data?.color ? data?.color : "gray");
      return {
        ...styles,
        backgroundColor: color.alpha(0.1).css(),
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: data.color,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: data.color,
      ":hover": {
        backgroundColor: data.color,
        color: "white",
      },
    }),
    input: (styles) => ({ ...styles, ...dot("transparent") }),
    placeholder: (styles) => ({
      ...styles,
      color: "#c8c8c8",
      fontFamily: "'DM Sans', sans-serif !important",
    }),
    singleValue: (styles, { data }) => ({
      ...styles,
      ...dot(data.color),
      color: theme.palette.mode === "light" ? "#000" : "#fff",
    }),
  };

  const { Option, SingleValue } = components;

  const ValueOption = (props: any) => (
    <SingleValue {...props}>
      <div style={{ display: "flex", alignItems: "center",fontSize:"16px",opacity: isDisabled ? 0.5 : 1}}>
        {props.data.icon && <span style={{ marginRight: "5px" }}>{props.data.icon}</span>}
        {props.data.label && <span>{props.data.label}</span>}
      </div>
    </SingleValue>
  );

  const MyOption = (props: any) => {
    const classes = useStyles();
    let a = [props.selectProps.inputValue];
    return (
      <Option {...props}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Highlighter
            highlightClassName={classes.highlight}
            searchWords={a}
            textToHighlight={props.data.label}
          />
        </div>
      </Option>
    );
  };

  const classes = useStyles();

  return (
    <div className={`${classes.Select} custom-mulitselect-dropdowns ${customClassNames}`} style={{ width }}>
      <Select
        isClearable
        menuPortalTarget={document.body}
        name={name}
        placeholder={placeholder}
        isDisabled={isDisabled}
        onFocus={() => onFocus && onFocus()}
        styles={
          styleProp
            ? styleProp
            : isError
            ? errorStyle
            : isColor
            ? colourStyles
            : customStyles
        }
        options={options}
        components={{ Option: MyOption, SingleValue: ValueOption }}
        value={defaultValue || null}
        onInputChange={(value, { action }) => {
          if (action === "input-change") {
            setInputValue(value);

            if (debounceRef.current) {
              clearTimeout(debounceRef.current);
            }

            debounceRef.current = setTimeout(() => {
              if (value.length >= minSearchChars) {
                getInput(value);
              }
            }, 500);
          }
        }}
        noOptionsMessage={() =>
          "No options"
        }
        onChange={(selected: any) => {
            handleChange(selected, name);
        }}
      />

      {isError && helperText && (
        <Typography variant="caption" className={mode.errInlineMsg}>
          {helperText}
        </Typography>
      )}
    </div>
  );
};

export default SearchableDropdown;